import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Table, Tag, Select, Input, Modal, Switch, Alert, message, DatePicker, TimePicker } from "antd"
import dayjs from "dayjs"

import Scheduling from "../../Apis/schedulingApi"
import {
  SLOT_STATE_TAGS,
  errorText,
  errorTextFrom,
  parseSchedulingError,
  formatDate,
  formatTimeRange,
  timezoneOptions,
  zoneOffsetLabel,
} from "./schedulingUtils"
import "./Scheduling.css"

/**
 * Setup card plus the dated slot list.
 *
 * Pause is a global booking setting; unpublished is a slot state — they are
 * separate controls on purpose. Busy status is recomputed on every load rather
 * than stored, so an event that disappears frees the time again.
 */
const AvailabilityTab = ({ t, lang, isRTL, settings, onSettingsChanged }) => {
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState("")

  const [slots, setSlots] = useState([])
  const [calendarStatus, setCalendarStatus] = useState("unavailable")
  const [loadingSlots, setLoadingSlots] = useState(false)

  const [addOpen, setAddOpen] = useState(false)
  const [newDate, setNewDate] = useState(null)
  const [newTime, setNewTime] = useState(null)
  const [addError, setAddError] = useState("")
  const [addFieldErrors, setAddFieldErrors] = useState({})
  const [publishing, setPublishing] = useState(false)
  const [refreshingConnection, setRefreshingConnection] = useState(false)

  const zones = useMemo(() => timezoneOptions(), [])

  useEffect(() => {
    if (settings) setDraft({ ...settings })
  }, [settings])

  const loadSlots = useCallback(async () => {
    try {
      setLoadingSlots(true)
      const response = await Scheduling.getSlots()
      setSlots(response?.data?.slots || [])
      setCalendarStatus(response?.data?.calendar_status || "unavailable")
    } catch (error) {
      message.error(errorTextFrom(t, error, t?.error_load_slots))
    } finally {
      setLoadingSlots(false)
    }
  }, [t])

  useEffect(() => {
    loadSlots()
  }, [loadSlots])

  if (!draft) return null

  // Slots are entered and listed in the *saved* organizer zone, because that is
  // what the backend converts them with. Labelling them with an unsaved draft
  // zone would publish a different instant than the admin was shown.
  const timezone = settings.timezone || "UTC"
  const dirty =
    draft.contact_email !== settings.contact_email ||
    draft.timezone !== settings.timezone ||
    draft.duration_minutes !== settings.duration_minutes ||
    draft.enabled !== settings.enabled

  const save = async () => {
    if (saving) return
    try {
      setSaving(true)
      setSaveError("")
      const response = await Scheduling.updateSettings({
        version: settings.version,
        enabled: draft.enabled,
        contact_email: draft.contact_email,
        duration_minutes: draft.duration_minutes,
        ...(draft.timezone ? { timezone: draft.timezone } : {}),
      })
      onSettingsChanged?.(response?.data)
      message.success(t?.settings_saved || "Settings saved")
    } catch (error) {
      const parsed = parseSchedulingError(error)
      setSaveError(errorText(t, parsed, t?.error_save_settings))
      if (parsed.code === "settings_changed") onSettingsChanged?.()
    } finally {
      setSaving(false)
    }
  }

  const discard = () => {
    Modal.confirm({
      title: t?.discard_title || "Discard your changes?",
      okText: t?.discard_ok || "Discard",
      cancelText: t?.keep_editing || "Keep editing",
      centered: true,
      onOk: () => setDraft({ ...settings }),
    })
  }

  /**
   * Setup runs in its own tab; this one keeps its place and its state.
   *
   * The tab is opened synchronously from the click, before awaiting anything,
   * because a browser only treats `window.open` as user-initiated inside the
   * click handler. It is navigated to the backend's binding start URL once the
   * API answers.
   */
  const connect = async () => {
    const setupTab = window.open("", "_blank")
    try {
      const response = await Scheduling.connectGoogle()
      const url = response?.data?.authorization_url
      if (!url) throw new Error("no authorization url")
      if (setupTab && !setupTab.closed) {
        setupTab.location.href = url
        return
      }
      message.warning(
        t?.setup_tab_blocked ||
          "Allow pop-ups for this site, then choose Connect Google Calendar again."
      )
    } catch (error) {
      try {
        setupTab?.close()
      } catch {
        /* the tab is already gone */
      }
      message.error(errorTextFrom(t, error, t?.error_connect))
    }
  }

  /** Read the connection back after finishing setup in the other tab. */
  const refreshConnection = async () => {
    try {
      setRefreshingConnection(true)
      const response = await Scheduling.getSettings()
      onSettingsChanged?.(response?.data)
      message.success(t?.connection_refreshed || "Connection status updated")
    } catch (error) {
      message.error(errorTextFrom(t, error, t?.error_load_settings))
    } finally {
      setRefreshingConnection(false)
    }
  }

  const publish = async () => {
    if (publishing) return
    const errors = {}
    if (!newDate) errors.date = t?.error_date_required
    if (!newTime) errors.start_time = t?.error_time_required
    setAddFieldErrors(errors)
    if (Object.keys(errors).length) return

    try {
      setPublishing(true)
      setAddError("")
      await Scheduling.publishSlot({
        date: newDate.format("YYYY-MM-DD"),
        startTime: newTime.format("HH:mm"),
      })
      message.success(t?.slot_published || "Time published")
      setAddOpen(false)
      setNewDate(null)
      setNewTime(null)
      loadSlots()
    } catch (error) {
      const parsed = parseSchedulingError(error)
      if (parsed.fieldErrors) setAddFieldErrors(parsed.fieldErrors)
      if (parsed.conflict) {
        // Only the interval is named — never what is on the calendar.
        setAddError(
          `${errorText(t, parsed, t?.error_slot_overlap)} (${formatTimeRange(
            parsed.conflict.start_at,
            parsed.conflict.end_at,
            timezone,
            lang
          )})`
        )
      } else {
        setAddError(errorText(t, parsed, t?.error_publish_slot))
      }
    } finally {
      setPublishing(false)
    }
  }

  const togglePublication = async (slot) => {
    try {
      await Scheduling.setSlotPublication(slot.id, {
        isPublished: !slot.is_published,
        version: slot.version,
      })
      loadSlots()
    } catch (error) {
      message.error(errorTextFrom(t, error, t?.error_generic))
      loadSlots()
    }
  }

  const columns = [
    {
      title: t?.slot_date || "Date",
      dataIndex: "start_at",
      render: (value) => formatDate(value, timezone, lang),
    },
    {
      title: t?.slot_time || "Time",
      key: "time",
      render: (_value, row) => (
        <span dir="ltr">{formatTimeRange(row.start_at, row.end_at, timezone, lang)}</span>
      ),
    },
    {
      title: t?.slot_timezone || "Time zone",
      dataIndex: "timezone",
      render: (value) => <span dir="ltr">{value || timezone}</span>,
    },
    {
      title: t?.slot_status || "Status",
      dataIndex: "state",
      render: (value) => {
        const tag = SLOT_STATE_TAGS[value] || SLOT_STATE_TAGS.unknown
        return <Tag color={tag.color}>{t?.[tag.key] || value}</Tag>
      },
    },
    {
      title: t?.table_actions || "Actions",
      key: "actions",
      render: (_value, row) => {
        // A booked or in-progress time is never unpublished as a way of
        // cancelling the meeting behind it.
        const locked = ["booked", "booking_in_progress", "past"].includes(row.state)
        if (locked) return <span className="sch-muted-action">—</span>
        return (
          <button type="button" className="sch-link-btn" onClick={() => togglePublication(row)}>
            {row.is_published ? t?.unpublish || "Unpublish" : t?.republish || "Republish"}
          </button>
        )
      },
    },
  ]

  const connected = settings.connected
  const needsReconnect = settings.connection_state === "reconnect_required"

  return (
    <div className="sch-tab">
      <section className="sch-card">
        <div className="sch-card-head">
          <div>
            <h2 className="sch-card-title">{t?.setup_title || "Booking setup"}</h2>
            <p className="sch-muted">
              {t?.setup_subtitle ||
                "One Zory calendar organizes every meeting booked from the website or from here."}
            </p>
          </div>
          <label className="sch-toggle">
            <Switch
              checked={draft.enabled}
              disabled={!connected || !draft.timezone}
              onChange={(checked) => setDraft((prev) => ({ ...prev, enabled: checked }))}
            />
            <span>{draft.enabled ? t?.bookings_enabled || "Bookings enabled" : t?.bookings_paused || "Bookings paused"}</span>
          </label>
        </div>

        {needsReconnect ? (
          <Alert
            type="error"
            showIcon
            className="sch-alert"
            message={t?.reconnect_title || "Google Calendar access was lost"}
            description={
              t?.reconnect_body ||
              "Reconnect to accept new bookings. Existing meetings and their Google events are unchanged — nothing has been cancelled."
            }
          />
        ) : null}

        {!connected ? (
          <Alert
            type="warning"
            showIcon
            className="sch-alert"
            message={t?.not_connected_title || "No calendar is connected"}
            description={
              t?.not_connected_body ||
              "Set the organizer time zone and connect a Google account that may create events and Google Meet conferences. Times cannot be published until both are done."
            }
          />
        ) : null}

        <div className="sch-connection">
          <span className="sch-google-mark">G</span>
          <span className="sch-connection-identity">
            <strong dir="ltr">
              {settings.google_account_email || t?.not_connected || "Not connected"}
            </strong>
            <span className="sch-muted">
              {connected
                ? `${t?.calendar || "Calendar"}: ${settings.google_calendar_id}`
                : t?.no_organizer || "No organizer account"}
            </span>
          </span>
          {connected ? (
            <Tag color={needsReconnect ? "error" : "success"}>
              {needsReconnect
                ? t?.connection_reconnect || "Reconnect required"
                : t?.connection_ok || "Connection healthy"}
            </Tag>
          ) : null}
          <span className="sch-connection-actions">
            <button type="button" className="sch-btn sch-btn-secondary" onClick={connect}>
              {connected ? t?.reconnect || "Reconnect" : t?.connect || "Connect Google Calendar"}
            </button>
            <button
              type="button"
              className="sch-btn sch-btn-secondary"
              onClick={refreshConnection}
              disabled={refreshingConnection}
            >
              {refreshingConnection
                ? t?.refreshing || "Refreshing…"
                : t?.refresh_connection || "Refresh connection status"}
            </button>
          </span>
        </div>

        <p className="sch-note">
          {t?.setup_tab_note ||
            "Setup opens in a new tab. Finish there, close it, and refresh connection status here."}{" "}
          {t?.reconnect_note ||
            "Reconnecting must use the same account — it never silently switches calendars."}
        </p>

        <div className="sch-setup-grid">
          <div className="sch-field">
            <label className="sch-label" htmlFor="sch-contact-email">
              {t?.contact_email || "Contact email shown on Contact Us"}
            </label>
            <Input
              id="sch-contact-email"
              dir="ltr"
              value={draft.contact_email}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, contact_email: event.target.value }))
              }
            />
            <p className="sch-hint">
              {t?.contact_email_hint ||
                "Shown to visitors. The invitation is sent by the connected organizer account."}
            </p>
          </div>

          <div className="sch-field">
            <label className="sch-label" htmlFor="sch-timezone">
              {t?.organizer_timezone || "Organizer time zone"} *
            </label>
            <Select
              id="sch-timezone"
              showSearch
              className="sch-select"
              value={draft.timezone || undefined}
              placeholder={t?.select_timezone || "Select a time zone"}
              onChange={(value) => setDraft((prev) => ({ ...prev, timezone: value }))}
              options={zones.map((zone) => ({ value: zone, label: zone }))}
            />
            <p className="sch-hint">
              {t?.timezone_hint ||
                "Changing this affects entry and display only. Existing slots and meetings keep their instants."}
              {draft.timezone ? ` ${zoneOffsetLabel(draft.timezone)}` : ""}
            </p>
          </div>

          <div className="sch-field">
            <label className="sch-label" htmlFor="sch-duration">
              {t?.duration || "Default meeting duration"}
            </label>
            <Select
              id="sch-duration"
              className="sch-select"
              value={draft.duration_minutes}
              onChange={(value) => setDraft((prev) => ({ ...prev, duration_minutes: value }))}
              options={(settings.duration_choices || [30]).map((minutes) => ({
                value: minutes,
                label: `${minutes} ${t?.minutes || "minutes"}`,
              }))}
            />
            <p className="sch-hint">
              {t?.duration_hint ||
                "Applies to new slots only. Existing slots and meetings keep their duration."}
            </p>
          </div>

          <div className="sch-field">
            <label className="sch-label">{t?.horizon || "Booking horizon"}</label>
            <Input value={`${settings.horizon_days} ${t?.days || "days"}`} disabled />
          </div>
        </div>

        {saveError ? <Alert type="error" showIcon message={saveError} className="sch-alert" /> : null}

        <div className="sch-card-actions">
          {dirty ? <span className="sch-unsaved">{t?.unsaved || "Unsaved changes"}</span> : null}
          <span className="sch-card-actions-right">
            <button
              type="button"
              className="sch-btn sch-btn-secondary"
              disabled={!dirty || saving}
              onClick={discard}
            >
              {t?.discard || "Discard"}
            </button>
            <button
              type="button"
              className="sch-btn sch-btn-primary"
              disabled={!dirty || saving}
              onClick={save}
            >
              {saving ? t?.saving || "Saving…" : t?.save_changes || "Save changes"}
            </button>
          </span>
        </div>
      </section>

      <section className="sch-card">
        <div className="sch-card-head">
          <div>
            <h2 className="sch-card-title">{t?.slots_title || "Published times"}</h2>
            <p className="sch-muted">
              {dirty
                ? t?.save_before_publishing ||
                  "Save your settings before publishing times — new times use the saved organizer time zone."
                : calendarStatus === "ok"
                  ? t?.slots_subtitle ||
                    "Busy status is read from the calendar each time this list loads; it is not stored."
                  : t?.slots_calendar_unavailable ||
                    "Busy status cannot be checked right now, so availability is not offered to visitors."}
            </p>
          </div>
          <span className="sch-card-actions-right">
            <button type="button" className="sch-btn sch-btn-secondary" onClick={loadSlots}>
              {t?.refresh || "Refresh"}
            </button>
            <button
              type="button"
              className="sch-btn sch-btn-primary"
              disabled={!connected || !settings.timezone || dirty}
              title={dirty ? t?.save_before_publishing : undefined}
              onClick={() => setAddOpen(true)}
            >
              {t?.add_slot || "Add slot"}
            </button>
          </span>
        </div>

        <Table
          columns={columns}
          dataSource={slots}
          rowKey="id"
          loading={loadingSlots}
          pagination={false}
          scroll={{ x: 700 }}
          locale={{ emptyText: t?.slots_empty || "No times published yet." }}
        />
      </section>

      <Modal
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        footer={null}
        centered
        width={520}
        title={t?.add_slot_title || "Add a bookable time"}
        className="scheduling-modal"
      >
        <div className="sch-setup-grid">
          <div className="sch-field">
            <label className="sch-label">{t?.slot_date || "Date"} *</label>
            <DatePicker
              className="sch-select"
              value={newDate}
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current < dayjs().startOf("day")}
              onChange={setNewDate}
            />
            {addFieldErrors.date ? (
              <p className="sch-field-error">{String(addFieldErrors.date)}</p>
            ) : null}
          </div>
          <div className="sch-field">
            <label className="sch-label">{t?.slot_start || "Start time"} *</label>
            <TimePicker
              className="sch-select"
              value={newTime}
              format="HH:mm"
              minuteStep={5}
              onChange={setNewTime}
            />
            {addFieldErrors.start_time ? (
              <p className="sch-field-error">{String(addFieldErrors.start_time)}</p>
            ) : null}
          </div>
          <div className="sch-field">
            <label className="sch-label">{t?.slot_ends || "Ends"}</label>
            <Input
              disabled
              value={
                newTime ? newTime.add(settings.duration_minutes, "minute").format("HH:mm") : "—"
              }
            />
            <p className="sch-hint">
              {t?.slot_ends_hint ||
                "Computed from the saved default duration. Save a new default before publishing to use it."}
            </p>
          </div>
        </div>

        <p className="sch-hint">
          {(t?.slot_entry_note || "Entered in the organizer time zone") +
            `: ${timezone} ${zoneOffsetLabel(timezone)}`}
        </p>

        {addError ? <Alert type="error" showIcon message={addError} className="sch-alert" /> : null}

        <div className="sch-modal-actions">
          <button type="button" className="sch-btn sch-btn-secondary" onClick={() => setAddOpen(false)}>
            {t?.cancel || "Cancel"}
          </button>
          <button
            type="button"
            className="sch-btn sch-btn-primary"
            disabled={publishing}
            onClick={publish}
          >
            {publishing ? t?.publishing || "Publishing…" : t?.publish_slot || "Publish slot"}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default AvailabilityTab
